import { AUDITED_TYPE_CONSOLIDATED, AUDITED_TYPE_STANDALONE, FINANCIAL_NATURE } from '../../../../utils/constants/commonConstants';

/**
 * Derives the default audited type (Consolidated / Standalone) from latest FY values.
 * - Both null → null
 * - Only one non-null → that one
 * - Both non-null, standaloneFY > consolidatedFY → Standalone
 * - Otherwise → Consolidated (tie and all other cases favour Consolidated per spec)
 */
export function deriveAuditedType(consolidatedFY: number | null, standaloneFY: number | null): string | null {
    if (consolidatedFY === null && standaloneFY === null) return null;
    if (consolidatedFY === null) return AUDITED_TYPE_STANDALONE;
    if (standaloneFY === null) return AUDITED_TYPE_CONSOLIDATED;
    return standaloneFY > consolidatedFY ? AUDITED_TYPE_STANDALONE : AUDITED_TYPE_CONSOLIDATED;
}

export type FinancialNature = (typeof FINANCIAL_NATURE)[keyof typeof FINANCIAL_NATURE];

/** Reads the `financialNature` URL param case-insensitively; anything unrecognised is ignored (null). */
export function parseFinancialNatureParam(raw: string | null): FinancialNature | null {
    const upper = raw?.trim().toUpperCase();
    return Object.values(FINANCIAL_NATURE).find((nature) => nature === upper) ?? null;
}

/** Maps an API nature ('CONSOLIDATED') back to its display type ('Consolidated'). */
export const natureToAuditedType = (nature: FinancialNature): string =>
    nature === FINANCIAL_NATURE.CONSOLIDATED ? AUDITED_TYPE_CONSOLIDATED : AUDITED_TYPE_STANDALONE;

/**
 * The type a surface actually shows: the selection when that nature has data on the surface,
 * otherwise the nature it does have; null when it has neither.
 */
export function pickEffectiveAuditedType(
    selected: string | null,
    consolidatedFY: number | null,
    standaloneFY: number | null,
): string | null {
    if (selected === AUDITED_TYPE_CONSOLIDATED && consolidatedFY !== null) return AUDITED_TYPE_CONSOLIDATED;
    if (selected === AUDITED_TYPE_STANDALONE && standaloneFY !== null) return AUDITED_TYPE_STANDALONE;
    return deriveAuditedType(consolidatedFY, standaloneFY);
}
